import { Injectable, inject } from '@angular/core'
import { ViewRecordsValueUpdateService } from '@app/views/services/view-records-value-update.service'
import { ViewGroupService } from '@app/views/view-controls'
import { isNonNull } from '@core/global-util'
import {
    AggregateFunctionConfig,
    MainBusinessRecord,
    Cell,
    CellEntities,
    Field,
    RecordGroup,
    View,
    ViewData,
} from '@models/ui'
import { SortDirection } from '@models/ui/sort.model'
import { RecordCellsService } from '@services/record-cells.service'
import { RecordFacadeService, ViewFacadeService } from '@services/store-facade'
import { ReplaySubject, filter, switchMap, combineLatest, of } from 'rxjs'
import { tap } from 'rxjs/operators'

@Injectable({
    providedIn: 'root',
})
export class ViewDataService {
    //TODO rewrite to observables https://chat.anveo.com/git/AnveoTikibase_ui/issues/2868
    private readonly recordFacadeService = inject(RecordFacadeService)
    private readonly viewFacadeService = inject(ViewFacadeService)
    private readonly viewGroupService = inject(ViewGroupService)
    private readonly recordCellsService = inject(RecordCellsService)
    private readonly viewRecordsValueUpdateService = inject(ViewRecordsValueUpdateService)

    private viewData!: ViewData & { selected_view: View }
    private cells!: { [recordGuid: string]: CellEntities }
    private groupDirection!: SortDirection

    readonly viewDataChanges$ = new ReplaySubject<void>(1)

    groups!: RecordGroup[] | undefined

    constructor() {
        this.recordFacadeService
            .selectViewData$()
            .pipe(
                filter(isNonNull),
                tap((data) => {
                    this.viewRecordsValueUpdateService.callDeletedUsers(data.data)
                }),
                switchMap((data) =>
                    combineLatest([
                        of(data),
                        this.viewGroupService.getGroupByView$(data.selected_view!),
                    ]),
                ),
            )
            .subscribe(([data, groupObject]) => {
                this.viewData = data

                this.groupDirection = groupObject.sortDirection!

                this.groups = this.setGroups()
                this.prepareCells()
                this.viewDataChanges$.next()
            })
    }

    get records(): MainBusinessRecord[] | undefined {
        if (this.viewData && this.viewData.data instanceof Array) {
            return this.viewData.data
        }
        return
    }

    getViewData() {
        return this.viewData
    }

    getCells() {
        return this.cells
    }

    isViewEmpty() {
        return (
            (!this.records || this.records.length === 0) &&
            (!this.groups || this.groups.length === 0)
        )
    }

    private prepareCells() {
        if (this.records?.length) {
            this.cells = this.recordCellsService.prepareCellsForRecords(this.records)
        } else if (this.groups?.length) {
            this.cells = this.recordCellsService.prepareFromGroups(this.groups)
        }
    }

    pinColumn(guid: string) {
        return this.viewFacadeService.updateViewRequest(this.viewData.selected_view, {
            columns_pinned: {
                cell: this.viewData.selected_view.columns_pinned,
                new_value: guid,
            },
        })
    }

    unPinColumn(guid: string) {
        const previousPinned = this.viewData.columns.pinned_columns || []
        previousPinned.splice(previousPinned.indexOf(guid), 1)

        return this.viewFacadeService.updateViewRequest(this.viewData.selected_view, {
            columns_pinned: {
                cell: this.viewData.selected_view.columns_pinned,
                new_value: previousPinned.join(','),
            },
        })
    }

    hideColumn(guid: string) {
        const previousHidden = this.viewData.columns.hidden_columns || []
        if (previousHidden.indexOf(guid) == -1) {
            previousHidden.push(guid)
        }

        return this.viewFacadeService.updateViewRequest(this.viewData.selected_view, {
            columns_hide: {
                cell: this.viewData.selected_view.columns_hide,
                new_value: previousHidden.join(','),
            },
        })
    }

    resetColumnWidth(guid: string) {
        const columnsWidth = { ...this.viewData?.columns.columns_width }
        delete columnsWidth[guid]

        return this.viewFacadeService.updateViewRequest(this.viewData?.selected_view as View, {
            columns_width: {
                cell: this.viewData?.selected_view?.columns_width as Cell,
                new_value: JSON.stringify(columnsWidth),
            },
        })
    }

    resizeColumn(guid: string, width: number) {
        const columnsWidthValue = this.viewData?.columns.columns_width
            ? {
                  ...this.viewData?.columns.columns_width,
                  [guid]: width,
              }
            : { [guid]: width }

        return this.viewFacadeService.updateViewRequest(this.viewData.selected_view, {
            columns_width: {
                cell: this.viewData.selected_view.columns_width,
                new_value: JSON.stringify(columnsWidthValue),
            },
        })
    }

    updateFieldAggregation(field: Field, aggregation: AggregateFunctionConfig) {
        return this.viewFacadeService.updateViewRequest(this.viewData.selected_view, {
            aggregate_functions: {
                cell: this.viewData.selected_view.aggregate_functions,
                new_value: JSON.stringify({
                    ...this.viewData.aggregate_function_configs,
                    [field.guid]: aggregation,
                }),
            },
        })
    }

    clearFieldAggregation(field: Field) {
        const fields = { ...this.viewData.aggregate_function_configs }
        delete fields[field.guid]

        return this.viewFacadeService.updateViewRequest(this.viewData.selected_view, {
            aggregate_functions: {
                cell: this.viewData.selected_view.aggregate_functions,
                new_value: JSON.stringify(fields),
            },
        })
    }

    columnHasCustomWidth(guid: string) {
        return !!this.viewData.columns.columns_width?.[guid]
    }

    hasPinned() {
        return !!this.viewData?.selected_view?.columns_pinned?.value?.length
    }

    isPinned(guid: string) {
        return !!this.viewData?.columns.pinned_columns?.includes(guid)
    }

    private setGroups() {
        if (this.viewData && this.viewData.data instanceof Map) {
            if (this.groupDirection === SortDirection.DESC) return [...this.viewData.data.values()]

            return [...this.viewData.data.values()].reverse()
        }
        return
    }
}
